import { useEffect, useMemo, useState } from "react";

import type { AdminUserRowSchema } from "@/api/generated/types.gen";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUpdateAdminUser } from "@/hooks/mutations/use-admin-users";
import { useAdminUsers } from "@/hooks/queries/use-admin-users";
import { useMe } from "@/hooks/queries/use-me";

/** Debounce a changing value by `delay` ms. */
const useDebounced = <T,>(value: T, delay = 300): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

/** The identity roles v1 access levels derive capabilities from. */
const USER_TYPES = ["internal", "expert", "referrer", "provider"] as const;

const UserRow = ({ user }: { user: AdminUserRowSchema }) => {
  const update = useUpdateAdminUser();

  return (
    <TableRow>
      <TableCell>{user.email}</TableCell>
      <TableCell>{user.name}</TableCell>
      <TableCell>
        <select
          className="h-9 min-w-[120px] rounded-md border px-2 text-sm"
          value={user.user_type ?? "internal"}
          disabled={update.isPending}
          onChange={(e) =>
            update.mutate({
              userId: user.id,
              body: { user_type: e.target.value as (typeof USER_TYPES)[number] },
            })
          }
        >
          {USER_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </TableCell>
      <TableCell>{user.auth_type ?? "—"}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Badge variant={user.is_admin ? "success" : "secondary"}>
            {user.is_admin ? "admin" : "no"}
          </Badge>
          <Button
            variant="outline"
            onClick={() => {
              const next = !user.is_admin;
              if (
                next &&
                !window.confirm(
                  `Grant admin to ${user.email}? Admins bypass the approval list and Vinci checks.`
                )
              ) {
                return;
              }
              update.mutate({ userId: user.id, body: { is_admin: next } });
            }}
            disabled={update.isPending}
          >
            {user.is_admin ? "Revoke admin" : "Make admin"}
          </Button>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          {user.is_blocked && <Badge variant="warning">blocked</Badge>}
          <Button
            variant={user.is_blocked ? "outline" : "destructive"}
            onClick={() =>
              update.mutate({
                userId: user.id,
                body: { is_blocked: !user.is_blocked },
              })
            }
            disabled={update.isPending}
          >
            {user.is_blocked ? "Unblock" : "Block"}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const UsersAdminPage = () => {
  const { data: me, isLoading: meLoading } = useMe();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search);

  const isAdmin = Boolean(me?.user?.is_admin);
  const { data: users = [], isLoading } = useAdminUsers({
    q: debouncedSearch || undefined,
    enabled: isAdmin,
  });

  const heading = useMemo(() => `Users (${users.length})`, [users.length]);

  if (meLoading) {
    return <div className="p-6">Loading…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="text-muted-foreground p-6">
        You don’t have access to this page.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <h1 className="text-xl font-semibold">{heading}</h1>
      <p className="text-muted-foreground text-sm">
        All users (internal + external). User type sets the access level:{" "}
        <strong>internal</strong>/<strong>expert</strong> get the full app,{" "}
        <strong>referrer</strong> and <strong>provider</strong> are restricted to
        their own cases (case creation + assessment prep + document requests). Admins
        bypass everything.
      </p>

      <Input
        className="max-w-[280px]"
        placeholder="Filter by email or name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>User type</TableHead>
            <TableHead>Auth</TableHead>
            <TableHead>Admin</TableHead>
            <TableHead>Blocked</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6}>Loading…</TableCell>
            </TableRow>
          ) : users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>No users found.</TableCell>
            </TableRow>
          ) : (
            users.map((user) => <UserRow key={user.id} user={user} />)
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default UsersAdminPage;
